import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { money } from "@/lib/currency";
import { orderNo } from "@/lib/order";
import { Check, Clock, ChefHat, Bell, PartyPopper, Loader2, Ban } from "lucide-react";
import { useT } from "@/lib/i18n";

const STEPS = [
  { key: "pending", label: "stepPending", icon: Clock },
  { key: "preparing", label: "stepPreparing", icon: ChefHat },
  { key: "ready", label: "stepReady", icon: Bell },
  { key: "delivered", label: "stepDelivered", icon: PartyPopper },
];

export const OrderTracker = ({ orderId, accent = "#D9531E", currency, initial = null }) => {
  const { t } = useT();
  const [order, setOrder] = useState(initial);
  const [loading, setLoading] = useState(!initial);

  useEffect(() => {
    let active = true;
    const fetchOrder = () =>
      api
        .get(`/orders/${orderId}`)
        .then((r) => active && setOrder(r.data))
        .catch(() => {})
        .finally(() => active && setLoading(false));
    fetchOrder();
    const id = setInterval(fetchOrder, 5000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [orderId]);

  if (loading)
    return (
      <div className="py-10 grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: accent }} />
      </div>
    );
  if (!order) return <p className="text-center text-sm text-[#6B6560]">{t("orderNotFound")}</p>;

  const idx = STEPS.findIndex((s) => s.key === order.status);
  const cur = order.currency_symbol || currency;
  const cancelled = order.status === "cancelled";

  return (
    <div data-testid="order-tracker" className="space-y-5">
      {cancelled ? (
        <div data-testid="order-cancelled" className="bg-white rounded-2xl border border-red-200 p-5 fade-up">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono-j text-sm text-[#6B6560]">{t("orderLabel", { id: orderNo(order), table: order.table_number })}</span>
            <span data-testid="order-status-badge" className="shrink-0 whitespace-nowrap text-xs font-semibold px-3 py-1 rounded-full text-white bg-red-600">{t("statusCancelled")}</span>
          </div>
          <div className="flex gap-3 mt-4">
            <div className="h-10 w-10 rounded-full grid place-items-center bg-red-50 shrink-0"><Ban className="h-5 w-5 text-red-600" /></div>
            <div>
              <p className="font-sub font-semibold text-[#1F1D1B]">{t("cancelledTitle")}</p>
              <p data-testid="order-cancel-reason" className="text-sm text-[#6B6560] mt-0.5">{t("reason", { r: order.cancel_reason })}</p>
              <p className="text-xs text-[#6B6560] mt-2">{t("askStaff")}</p>
            </div>
          </div>
        </div>
      ) : (
      <div className="bg-white rounded-2xl border border-[#E6E1DA] p-5">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono-j text-sm text-[#6B6560]">{t("orderLabel", { id: orderNo(order), table: order.table_number })}</span>
          <span data-testid="order-status-badge" className="shrink-0 whitespace-nowrap text-xs font-semibold px-3 py-1 rounded-full text-white" style={{ backgroundColor: accent }}>
            {t(STEPS[Math.max(0, idx)].label)}
          </span>
        </div>
        <div className="mt-5">
          {STEPS.map((s, i) => {
            const done = i < idx;
            const current = i === idx;
            const active = i <= idx;
            return (
              <div key={s.key} className="flex gap-4" data-testid={`track-step-${s.key}`}>
                <div className="flex flex-col items-center">
                  <div
                    className={`h-10 w-10 rounded-full grid place-items-center transition-all ${current ? "pulse-pending" : ""}`}
                    style={active ? { backgroundColor: accent, color: "#fff" } : { backgroundColor: "#EFEAE2", color: "#B8AFA2" }}
                  >
                    {done ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                  </div>
                  {i < STEPS.length - 1 && <div className="w-0.5 flex-1 min-h-[26px] my-1 rounded-full" style={{ backgroundColor: i < idx ? accent : "#EFEAE2" }} />}
                </div>
                <div className="pb-5">
                  <p className={`font-sub font-semibold ${active ? "text-[#1F1D1B]" : "text-[#B8AFA2]"}`}>{t(s.label)}</p>
                  <p className="text-xs text-[#6B6560]">{t(`${s.label}Desc`)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {order.status === "ready" && (
        <div data-testid="ready-banner" className="rounded-2xl p-4 text-center text-white fade-up" style={{ backgroundColor: accent }}>
          <p className="font-sub font-bold">🔔 {t("readyTitle")}</p>
          <p className="text-xs mt-1 opacity-90">{t("readyDesc")}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E6E1DA] p-5">
        {order.items.map((it, i) => (
          <div key={i} className="flex justify-between text-sm mt-1.5 first:mt-0 text-[#1F1D1B]">
            <span>{it.qty}× {it.name}</span>
            <span className="font-mono-j">{money(it.price * it.qty, cur)}</span>
          </div>
        ))}
        {order.note && <p className="text-xs text-amber-600 mt-2 italic">"{order.note}"</p>}
        <div className="flex justify-between font-bold mt-3 pt-3 border-t border-[#E6E1DA] text-[#1F1D1B]">
          <span>{cancelled ? t("totalNotCharged") : t("totalPay")}</span>
          <span className="font-mono-j whitespace-nowrap">{money(order.total, cur)}</span>
        </div>
        {!cancelled && order.payment_qr_path && (
          <div data-testid="payment-qr" className="mt-4 pt-4 border-t border-dashed border-[#E6E1DA] flex flex-col items-center text-center">
            <p className="text-sm font-semibold text-[#1F1D1B]">{order.payment_qr_label ? t("scanToPayWith", { m: order.payment_qr_label }) : t("scanToPay")}</p>
            <img data-testid="payment-qr-image" src={fileUrl(order.payment_qr_path)} alt="Payment QR" className="mt-3 w-56 max-w-full rounded-xl border border-[#E6E1DA] bg-white p-2" />
            <p className="text-xs text-[#6B6560] mt-2">{t("amount")} <b className="font-mono-j text-[#1F1D1B]">{money(order.total, cur)}</b> · {t("showPayment")}</p>
          </div>
        )}
      </div>
    </div>
  );
};
